import { clsx } from '@alexis/helpers/clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Chevron from '@icons/chevron-left-medium.svg';

import { getIsRtlState } from '@redux/selectors';

import styles from './FilterBarCarousel.module.scss';

type FilterBarCarouselProps = {
  children: React.ReactNode;
};

const FilterBarCarousel: React.FC<FilterBarCarouselProps> = ({ children }) => {
  const isRtl = useSelector(getIsRtlState);

  const containerDivRef = useRef<HTMLDivElement>(null);

  const [isShowLeftArrow, setIsShowLeftArrow] = useState<boolean>(false);
  const [isShowRightArrow, setIsShowRightArrow] = useState<boolean>(false);

  const showHideScrollButtons = () => {
    if (containerDivRef.current) {
      const scrollLeft = containerDivRef.current.scrollLeft;
      const maxScrollLeft = containerDivRef.current.scrollWidth;

      setIsShowLeftArrow(isRtl ? scrollLeft < 0 : scrollLeft > 0);
      setIsShowRightArrow(
        isRtl
          ? maxScrollLeft + scrollLeft > containerDivRef.current.offsetWidth
          : containerDivRef.current.offsetWidth + scrollLeft < maxScrollLeft - 1,
      );
    }
  };

  useEffect(() => {
    if (containerDivRef.current) {
      showHideScrollButtons();
    }
  }, [containerDivRef, isRtl, children]);

  useEffect(() => {
    if (containerDivRef.current) {
      window.addEventListener('resize', showHideScrollButtons);

      return () => {
        window.removeEventListener('resize', showHideScrollButtons);
      };
    }
  }, []);

  const handleLeftChevronClick = () => {
    if (isRtl) {
      containerDivRef.current?.scrollTo({
        left: containerDivRef.current?.offsetWidth - containerDivRef.current?.scrollWidth,
        behavior: 'smooth',
      });
    } else {
      containerDivRef.current?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleRightChevronClick = () => {
    if (isRtl) {
      containerDivRef.current?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      containerDivRef.current?.scrollTo({
        left: containerDivRef.current?.scrollWidth - containerDivRef.current?.offsetWidth,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      ref={containerDivRef}
      className={clsx(styles.carouselContainer)}
      onScroll={showHideScrollButtons}
    >
      <div className={styles.carousel}>{children}</div>

      <button
        aria-label='Previous'
        type='button'
        className={styles.leftChevron}
        disabled={!isShowLeftArrow}
        onClick={isRtl ? handleRightChevronClick : handleLeftChevronClick}
      >
        <Chevron className={clsx(styles.chevron, isRtl && styles.right)} />
      </button>

      <button
        aria-label='Next'
        type='button'
        className={styles.rightChevron}
        disabled={!isShowRightArrow}
        onClick={isRtl ? handleLeftChevronClick : handleRightChevronClick}
      >
        <Chevron className={clsx(styles.chevron, !isRtl && styles.right)} />
      </button>
    </div>
  );
};

export default FilterBarCarousel;
